import { Injectable } from '@angular/core';
import { flatten } from 'lodash';
import { Person } from '../core-data-model/Person.model';
import { ContextDataService } from '../core-services/Context.dataservice';
import { Util } from '../core-utilities/Util';
import { TranslationService } from '../translations';

type PatternSetEntry = {
    SurveyUserGuid: string;
    Name: {
        Last: string;
        First: string;
    };
    ASigma: number;
    BSigma: number;
    CSigma: number;
    DSigma: number;
    ESigma: number;
};

@Injectable({ providedIn: 'root' })
export class PatternSetService {
    constructor(
        public TR: TranslationService,
        public context: ContextDataService,
        private utility: Util
    ) {}

    // TODO: Would love to make this immutable but sadly the typing in Group
    // Analytics is garbo so it's very hard to tell what would break when this
    // changes without manually combing through the code
    get patternSet(): PatternSetEntry[] {
        return this.context.currentSet
            .filter(
                (r): r is Person =>
                    r instanceof Person && r.hasBehavioralPattern()
            )
            .map((person) => {
                // TODO better filtering and assessment getting
                const self = person.self;
                return {
                    SurveyUserGuid: person.id,
                    Name: {
                        Last: person.lastName,
                        First: person.firstName
                    },
                    ASigma: self.A,
                    BSigma: self.B,
                    CSigma: self.C,
                    DSigma: self.D,
                    ESigma: self.E
                };
            })
            .toArray();
    }

    hasPeopleWithPatterns() {
        return this.patternSet.length > 0;
    }

    private makeArrayOfProperties(personObject: any, positionObject: any) {
        const combinedProperties: any = {};
        Object.keys(personObject).forEach((key) => {
            combinedProperties[key] = true;
        });
        if (this.utility.hasValue(positionObject)) {
            Object.keys(positionObject).forEach((key) => {
                combinedProperties[key] = true;
            });
        } else {
            combinedProperties.Name = '';
            combinedProperties.ASigma = '';
            combinedProperties.BSigma = '';
            combinedProperties.CSigma = '';
            combinedProperties.DSigma = '';
        }
        delete combinedProperties.SurveyUserId;
        delete combinedProperties['PersonPattern.SurveyUserId'];
        delete combinedProperties['PersonPattern.PiPatternGraphId'];
        delete combinedProperties['PersonPattern.MidPoint'];
        delete combinedProperties.SurveyUserGuid;
        delete combinedProperties.PositionId;
        delete combinedProperties.PositionGuid;
        return Object.keys(combinedProperties);
    }

    private headerLine(properties: any) {
        return properties
            .map((element: any) => this.makeCSVSafe(element))
            .join(',');
    }
    private basicLine(properties: any, thing: any) {
        return properties
            .map((element: any) => {
                const value = thing[element];
                if (this.utility.hasValue(value)) {
                    return this.makeCSVSafe(value.toString());
                }

                return '';
            })
            .join(',');
    }

    private makeCSVSafe(x: any) {
        let str = x.replace(/'/g, '""');
        if (/\r|\n|'|,/.exec(str)) {
            str = '"' + str + '"';
        }
        // rename headers for position values
        if (str === 'Name') {
            str = 'PositionName';
        }
        if (str === 'ASigma') {
            str = 'PositionPattern.ASigma';
        }
        if (str === 'BSigma') {
            str = 'PositionPattern.BSigma';
        }
        if (str === 'CSigma') {
            str = 'PositionPattern.CSigma';
        }
        if (str === 'DSigma') {
            str = 'PositionPattern.DSigma';
        }
        return str;
    }

    createCSVData(rawPatterns: any[], rawPosition: any): string {
        const patterns = rawPatterns.map((item) => flatten(item));
        const position = this.utility.hasValue(rawPosition)
            ? flatten(rawPosition)
            : undefined;
        const lineEnd = '\r\n';
        const properties = this.makeArrayOfProperties(patterns[0], position);
        let CSVData = this.headerLine(properties) + lineEnd;
        patterns.forEach((pattern) => {
            CSVData = CSVData + this.basicLine(properties, pattern) + lineEnd;
        });
        if (this.utility.hasValue(position)) {
            CSVData = CSVData + this.basicLine(properties, position) + lineEnd;
        }
        return CSVData;
    }
}
